/**
 * Federal registration detail — view and edit a SAM/UEI/CAGE registration (owner/office; gated
 * server-side). Reached by tapping a registration row on the Government hub. Refetches the
 * registrations list (real GET round-trip), seeds an editable form from the row, and Save issues the
 * real PATCH /api/gov/federal-registrations/:id, invalidating the list so the hub reflects the change.
 *
 * NEVER imports @smartbox/* (mobile invariant 1). Design-system primitives only; no raw styles.
 */
import { useState } from 'react';
import { ActivityIndicator, StyleSheet, View } from 'react-native';
import { useLocalSearchParams, useRouter } from 'expo-router';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  listRegistrations,
  updateFederalRegistration,
  type FederalRegistrationRow,
  type FederalRegistrationStatus,
  type UpdateFederalRegistrationRequest,
} from '../../../../src/api/gov';
import { Screen, Text, Card, Button, Field, Select } from '../../../../src/components/ui';
import { theme } from '../../../../src/theme';
import { extractErrorMessage } from '../../../../src/lib/format';

const REG_STATUSES: { value: FederalRegistrationStatus; label: string }[] = [
  { value: 'pending', label: 'Pending' },
  { value: 'active', label: 'Active' },
  { value: 'expired', label: 'Expired' },
];

/** Convert a YYYY-MM-DD field value into an ISO datetime, or undefined if blank/invalid. */
function toIso(v: string): string | undefined {
  const t = v.trim();
  if (!t) return undefined;
  const d = new Date(`${t}T00:00:00.000Z`);
  return Number.isNaN(d.getTime()) ? undefined : d.toISOString();
}

/** YYYY-MM-DD from an ISO string, or '' when null. */
function toDateField(v: string | null): string {
  if (!v) return '';
  const d = new Date(v);
  return Number.isNaN(d.getTime()) ? '' : d.toISOString().slice(0, 10);
}

/** Mounted only once the registration is loaded, so useState seeds from the fetched row. */
function RegistrationEditForm({ registration, onSaved }: { registration: FederalRegistrationRow; onSaved: () => void }) {
  const [uei, setUei] = useState(registration.uei ?? '');
  const [cage, setCage] = useState(registration.cage_code ?? '');
  const [status, setStatus] = useState<FederalRegistrationStatus>(
    (['active', 'expired', 'pending'].includes(registration.registration_status)
      ? registration.registration_status
      : 'pending') as FederalRegistrationStatus,
  );
  const [naics, setNaics] = useState(registration.primary_naics ?? '');
  const [regDate, setRegDate] = useState(toDateField(registration.registration_date));
  const [expDate, setExpDate] = useState(toDateField(registration.expiration_date));
  const [samUrl, setSamUrl] = useState(registration.sam_profile_url ?? '');
  const [error, setError] = useState<string | null>(null);

  const mut = useMutation({
    mutationFn: (body: UpdateFederalRegistrationRequest) => updateFederalRegistration(registration.id, body),
    onSuccess: () => { setError(null); onSaved(); },
    onError: (err) => setError(extractErrorMessage(err, 'Could not update the federal registration.')),
  });

  const onSave = () => {
    // Send null (not undefined) for cleared optional fields so an intentional clear persists.
    const body: UpdateFederalRegistrationRequest = {
      uei: uei.trim() || null,
      cage_code: cage.trim() || null,
      registration_status: status,
      primary_naics: naics.trim() || null,
      registration_date: toIso(regDate) ?? null,
      expiration_date: toIso(expDate) ?? null,
      sam_profile_url: samUrl.trim() || null,
    };
    mut.mutate(body);
  };

  return (
    <Card>
      <Field label="UEI" placeholder="e.g. ABC123DEF456" value={uei} onChangeText={setUei} autoCapitalize="characters" autoCorrect={false} accessibilityLabel="UEI" />
      <Field label="CAGE code" placeholder="e.g. 1ABC2" value={cage} onChangeText={setCage} autoCapitalize="characters" autoCorrect={false} accessibilityLabel="CAGE code" />
      <Text variant="label" color="secondary" style={styles.fieldLabel}>Status</Text>
      <Select options={REG_STATUSES} value={status} onChange={setStatus} />
      <Field label="Primary NAICS" placeholder="e.g. 236220" value={naics} onChangeText={setNaics} keyboardType="number-pad" accessibilityLabel="Primary NAICS" />
      <Field label="Registration date (YYYY-MM-DD)" placeholder="2026-01-01" value={regDate} onChangeText={setRegDate} autoCapitalize="none" autoCorrect={false} accessibilityLabel="Registration date" />
      <Field label="Expiration date (YYYY-MM-DD)" placeholder="2027-01-01" value={expDate} onChangeText={setExpDate} autoCapitalize="none" autoCorrect={false} accessibilityLabel="Expiration date" />
      <Field label="SAM profile URL" placeholder="https://sam.gov/…" value={samUrl} onChangeText={setSamUrl} autoCapitalize="none" autoCorrect={false} accessibilityLabel="SAM profile URL" />
      {error ? (
        <Text variant="bodyStrong" color="danger" accessibilityRole="alert">{error}</Text>
      ) : null}
      <Button title="Save" onPress={onSave} loading={mut.isPending} disabled={mut.isPending} />
    </Card>
  );
}

export function RegistrationDetailScreen() {
  const { id } = useLocalSearchParams<{ id: string }>();
  const router = useRouter();
  const queryClient = useQueryClient();

  const registrationsQuery = useQuery({ queryKey: ['gov-registrations'], queryFn: listRegistrations, staleTime: 30_000 });

  if (registrationsQuery.isLoading) {
    return (
      <Screen>
        <View style={styles.centered}>
          <ActivityIndicator size="large" color={theme.color.action.primary} accessibilityLabel="Loading registration" />
        </View>
      </Screen>
    );
  }

  if (registrationsQuery.isError) {
    return (
      <Screen>
        <Card>
          <Text variant="bodyStrong" color="danger" accessibilityRole="alert">
            {extractErrorMessage(registrationsQuery.error, 'Failed to load the registration.')}
          </Text>
          <Button title="Retry" variant="secondary" size="sm" fullWidth={false} onPress={() => registrationsQuery.refetch()} />
        </Card>
      </Screen>
    );
  }

  const registration = (registrationsQuery.data ?? []).find((r: FederalRegistrationRow) => r.id === id);
  if (!registration) {
    return (
      <Screen>
        <Card>
          <Text variant="body" color="secondary">Registration not found.</Text>
          <Button title="Back to government" variant="secondary" onPress={() => router.replace('/(app)/government')} />
        </Card>
      </Screen>
    );
  }

  return (
    <Screen scroll>
      <Text variant="title">Federal registration</Text>
      <RegistrationEditForm
        registration={registration}
        onSaved={() => { void queryClient.invalidateQueries({ queryKey: ['gov-registrations'] }); router.replace('/(app)/government'); }}
      />
      <Button title="Back to government" variant="secondary" onPress={() => router.replace('/(app)/government')} />
    </Screen>
  );
}

const styles = StyleSheet.create({
  centered: { flex: 1, justifyContent: 'center', alignItems: 'center', gap: theme.spacing.md },
  fieldLabel: { marginTop: theme.spacing.sm, marginBottom: theme.spacing.xs },
});
